import { ExclamationCircleOutlined } from "@ant-design/icons"
import dayjs from "dayjs"
import styled from "styled-components"

import AnimateInFlex, { ANIMATION_BY_KEY } from "#blocks/AnimateInFlex"
import TagWithTooltip from "#blocks/TagWithTooltip"
import ClickableStudentName from "#components/ClickableStudentName"
import {
  type ThemeContextType,
  useThemeContext,
} from "#contexts/ThemeContextProvider"
import { type StudentConstructor } from "#customTypes/composedTypes/studentTypes"
import { STUDENT_DOB_FORMAT } from "#utils/dateHelpers"

import Flex from "./buildingBlocks/Flex"

interface ClickableStudentInfoRowProps {
  student: StudentConstructor | null
  border?: boolean
}

const SFlex = styled(AnimateInFlex)<{
  theme: ThemeContextType
  border?: boolean
}>`
  border-radius: 6px;
  padding: 3px 8px;
  min-width: 0;
  height: fit-content;
  ${({ border }) => border && "border: 1px solid #ccc;"}
`

// If this is truncating names, it's usually because there is an emoji in the name
const ClickableStudentInfoRow = ({
  student,
  border = false,
}: ClickableStudentInfoRowProps) => {
  const theme = useThemeContext()

  if (!student) {
    return (
      <SFlex
        center
        gap={10}
        theme={theme}
        border={border}
        animation={ANIMATION_BY_KEY.leftSm}
      >
        <ExclamationCircleOutlined /> Student not found
      </SFlex>
    )
  }

  return (
    <SFlex
      center
      gap={10}
      theme={theme}
      border={border}
      animation={ANIMATION_BY_KEY.leftSm}
    >
      <img
        src={student?.avatar ?? "https://placehold.co/28x28"}
        style={{ width: "1.3rem", height: "1.3rem", borderRadius: "50%" }}
      />
      <Flex style={{ fontVariantNumeric: "tabular-nums" }}>
        {dayjs(student.dob).format(STUDENT_DOB_FORMAT)}
      </Flex>
      <ClickableStudentName studentId={student.id} label={student.name} />
      <Flex gap={2}>
        {student?.tags?.map((tagId) => (
          <TagWithTooltip key={tagId} tagName={tagId} />
        ))}
      </Flex>
    </SFlex>
  )
}

export default ClickableStudentInfoRow
